import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  onSnapshot
} from '../services/firestoreCompat';
import { db } from './firebaseConfig';

// Collection names
const RESERVATIONS_COLLECTION = 'reservations';
const TABLES_COLLECTION = 'restaurant_tables';

// Reservation interface
export interface CloudReservation {
  id?: string;
  table_number: number;
  customer_name: string;
  customer_phone: string;
  customer_email: string;
  reservation_time: string;
  party_size: number;
  special_requests: string;
  status: 'confirmed' | 'cancelled' | 'completed';
  created_at: any; // Firestore Timestamp
  updated_at: any; // Firestore Timestamp
}

// Table interface
export interface CloudTable {
  id?: string;
  number: number;
  capacity: number;
  status: 'available' | 'occupied' | 'reserved';
  area: 'indoor' | 'outdoor';
  customer_name?: string;
  total_amount?: number;
  item_count?: number;
  start_time?: any; // Firestore Timestamp or ISO string
  created_at: any; // Firestore Timestamp
  updated_at: any; // Firestore Timestamp
}

// Reservation Service
export class ReservationCloudService {
  // Create a new reservation
  static async createReservation(reservation: Omit<CloudReservation, 'id' | 'created_at' | 'updated_at'>): Promise<string> {
    try {
      const { serverTimestamp } = await import('../services/firestoreCompat');
      const reservationData = {
        ...reservation,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, RESERVATIONS_COLLECTION), reservationData);
      console.log('✅ Reservation created in cloud with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('❌ Error creating reservation in cloud:', error);
      throw error;
    }
  }

  // Get all reservations
  static async getReservations(): Promise<CloudReservation[]> {
    try {
      const q = query(collection(db, RESERVATIONS_COLLECTION), orderBy('created_at', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const reservations: CloudReservation[] = [];
      querySnapshot.forEach((doc) => {
        reservations.push({
          id: doc.id,
          ...doc.data()
        } as CloudReservation);
      });
      
      console.log('✅ Loaded reservations from cloud:', reservations.length);
      return reservations;
    } catch (error) {
      console.warn('⚠️ Error fetching reservations from cloud, returning empty array:', error);
      // Return empty array instead of throwing error to prevent app crashes
      return [];
    }
  }

  // Get reservations for a specific table
  static async getReservationsForTable(tableNumber: number): Promise<CloudReservation[]> {
    try {
      const q = query(
        collection(db, RESERVATIONS_COLLECTION), 
        where('table_number', '==', tableNumber),
        where('status', '==', 'confirmed'),
        orderBy('created_at', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      const reservations: CloudReservation[] = [];
      querySnapshot.forEach((doc) => {
        reservations.push({
          id: doc.id,
          ...doc.data()
        } as CloudReservation);
      });
      
      return reservations;
    } catch (error) {
      console.error('❌ Error fetching reservations for table:', error);
      throw error;
    }
  }

  // Update reservation status
  static async updateReservationStatus(reservationId: string, status: CloudReservation['status']): Promise<void> {
    try {
      const { serverTimestamp } = await import('../services/firestoreCompat');
      const reservationRef = doc(db, RESERVATIONS_COLLECTION, reservationId);
      await updateDoc(reservationRef, {
        status,
        updated_at: serverTimestamp()
      });
      console.log('✅ Reservation status updated in cloud');
    } catch (error) {
      console.error('❌ Error updating reservation status:', error);
      throw error;
    }
  }

  // Update reservation fields
  static async updateReservation(reservationId: string, data: Partial<Omit<CloudReservation, 'id' | 'created_at'>>): Promise<void> {
    try {
      const { serverTimestamp } = await import('../services/firestoreCompat');
      const reservationRef = doc(db, RESERVATIONS_COLLECTION, reservationId);
      await updateDoc(reservationRef, {
        ...data,
        updated_at: serverTimestamp()
      });
      console.log('✅ Reservation updated in cloud');
    } catch (error) {
      console.error('❌ Error updating reservation:', error);
      throw error;
    }
  }

  // Delete reservation
  static async deleteReservation(reservationId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, RESERVATIONS_COLLECTION, reservationId));
      console.log('✅ Reservation deleted from cloud');
    } catch (error) {
      console.error('❌ Error deleting reservation:', error);
      throw error;
    }
  }

  // Listen to reservations changes
  static onReservationsChange(callback: (reservations: CloudReservation[]) => void): () => void {
    const q = query(collection(db, RESERVATIONS_COLLECTION), orderBy('created_at', 'desc'));
    
    return onSnapshot(q, 
      (querySnapshot) => {
        const reservations: CloudReservation[] = [];
        querySnapshot.forEach((doc) => {
          reservations.push({
            id: doc.id,
            ...doc.data()
          } as CloudReservation);
        });
        callback(reservations);
      },
      (error) => {
        console.warn('⚠️ Error in reservations listener, returning empty array:', error);
        callback([]);
      }
    );
  }
}

// Table Service
export class TableCloudService {
  // Update table status
  static async updateTableStatus(tableId: string, status: CloudTable['status'], customerData?: {
    customer_name?: string;
    total_amount?: number;
    item_count?: number;
    start_time?: any; // Firestore Timestamp or ISO string
  }): Promise<void> {
    try {
      const { serverTimestamp } = await import('../services/firestoreCompat');
      const tableRef = doc(db, TABLES_COLLECTION, tableId);
      const updateData: any = {
        status,
        updated_at: serverTimestamp()
      };
      
      if (customerData) {
        Object.assign(updateData, customerData);
      }
      
      await updateDoc(tableRef, updateData);
      console.log('✅ Table status updated in cloud');
    } catch (error) {
      console.error('❌ Error updating table status:', error);
      throw error;
    }
  }

  // Get all tables
  static async getTables(): Promise<CloudTable[]> {
    try {
      const q = query(collection(db, TABLES_COLLECTION), orderBy('number', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const tables: CloudTable[] = [];
      querySnapshot.forEach((doc) => {
        tables.push({
          id: doc.id,
          ...doc.data()
        } as CloudTable);
      });
      
      console.log('✅ Loaded tables from cloud:', tables.length);
      return tables;
    } catch (error) {
      console.error('❌ Error fetching tables from cloud:', error);
      throw error;
    }
  }

  // Listen to tables changes
  static onTablesChange(callback: (tables: CloudTable[]) => void): () => void {
    const q = query(collection(db, TABLES_COLLECTION), orderBy('number', 'asc'));
    
    return onSnapshot(q, 
      (querySnapshot) => {
        const tables: CloudTable[] = [];
        querySnapshot.forEach((doc) => {
          tables.push({
            id: doc.id,
            ...doc.data()
          } as CloudTable);
        });
        callback(tables);
      },
      (error) => {
        console.warn('⚠️ Error in tables listener, returning empty array:', error);
        callback([]);
      }
    );
  }
}
